import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { DateTime } from 'luxon';
import {
  Box,
  Card,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { colors } from 'Constants/Colors';
import useTranslation from 'Hooks/useTranslation';
import { useHasPermission, SESSION_PERMISSIONS } from 'Utils/permissions';
import SessionStatusPill from './SessionStatusPill';
import { SESSION_TZ, durationMinutes, formatSessionDate, formatSessionTime } from '../utils/session-datetime';

const styles = {
  monthCard: {
    overflow: 'hidden',
    border: `1px solid ${colors.GRAY_HR}`,
    boxShadow: 'none',
    borderRadius: 1.5,
    mb: 2,
  },
  monthHeader: {
    px: 2.5,
    py: 1.75,
    bgcolor: 'rgb(245, 246, 250)',
    borderBottom: `1px solid ${colors.GRAY_HR}`,
    display: 'flex',
    alignItems: 'center',
    gap: 1.75,
  },
  monthTitle: { fontSize: 14.5, fontWeight: 600, color: 'rgb(31, 42, 58)' },
  monthSubtitle: { fontSize: 11.5, color: 'rgb(138, 147, 166)', mt: '2px' },
  statLabel: {
    fontSize: 11,
    color: 'rgb(138, 147, 166)',
    textTransform: 'uppercase',
    fontWeight: 600,
    letterSpacing: 0.4,
  },
  statValue: { fontSize: 14, color: 'rgb(31, 42, 58)', fontWeight: 700, textAlign: 'right' },
  headerCell: {
    bgcolor: '#fff',
    fontSize: 11,
    fontWeight: 600,
    color: colors.GRAY_DARK,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    py: 1.4,
  },
  row: {
    cursor: 'pointer',
    transition: 'background 0.15s',
    '&:hover': { bgcolor: colors.custom.table.row.hover },
  },
  titleCell: { fontWeight: 500, color: 'rgb(31, 42, 58)', fontSize: 13.5 },
  description: { fontSize: 11.5, color: 'rgb(138, 147, 166)', mt: '2px' },
  cell: { fontSize: 12.5, color: 'rgb(74, 85, 104)', whiteSpace: 'nowrap' },
};

const formatMonthLabel = monthKey => {
  // monthKey = 'yyyy-LL'
  const dt = DateTime.fromFormat(monthKey, 'yyyy-LL', { zone: SESSION_TZ }).setLocale('es-MX');
  if (!dt.isValid) return monthKey;
  const raw = dt.toFormat('LLLL yyyy');
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const groupByMonth = sessions => {
  const map = new Map();
  sessions.forEach(session => {
    const dt = DateTime.fromISO(session.startDateTime, { zone: 'utc' }).setZone(SESSION_TZ);
    if (!dt.isValid) return;
    const key = dt.toFormat('yyyy-LL');
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(session);
  });

  return Array.from(map.entries())
    .sort(([a], [b]) => (a < b ? 1 : -1)) // most recent month first
    .map(([key, items]) => {
      const totalMinutes = items.reduce((acc, s) => acc + (durationMinutes(s.startDateTime, s.endDateTime) ?? 0), 0);
      return {
        key,
        label: formatMonthLabel(key),
        sessions: items,
        totalSessions: items.length,
        totalHours: totalMinutes / 60,
      };
    });
};

export default function SessionsMonthlyView({ sessions, loading, onOpenDetail, onEdit, onDelete }) {
  const { t2 } = useTranslation('EditCourseSessions');
  const canUpdate = useHasPermission(SESSION_PERMISSIONS.UPDATE);
  const canDelete = useHasPermission(SESSION_PERMISSIONS.DELETE);

  const groups = useMemo(() => groupByMonth(sessions || []), [sessions]);

  if (loading) {
    return <LinearProgress sx={{ height: 2, mb: 2 }} />;
  }

  if (groups.length === 0) {
    return (
      <Card sx={{ p: 4, textAlign: 'center', border: `1px solid ${colors.GRAY_HR}`, boxShadow: 'none' }} elevation={0}>
        <Typography variant="body2" sx={{ color: colors.GRAY_DARK }}>
          {t2('empty.noSessions')}
        </Typography>
      </Card>
    );
  }

  return (
    <Box>
      {groups.map(group => (
        <Card key={group.key} sx={styles.monthCard} elevation={0}>
          <Box sx={styles.monthHeader}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={styles.monthTitle}>{group.label}</Typography>
              <Typography sx={styles.monthSubtitle}>
                {t2('monthly.sessionsCount', [String(group.totalSessions)])}
              </Typography>
            </Box>
            <Stack direction="row" spacing={2.75} alignItems="center">
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={styles.statLabel}>{t2('monthly.headerSessions')}</Typography>
                <Typography sx={styles.statValue}>{group.totalSessions}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={styles.statLabel}>{t2('monthly.headerHours')}</Typography>
                <Typography sx={styles.statValue}>{group.totalHours.toFixed(1)}h</Typography>
              </Box>
            </Stack>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={styles.headerCell}>{t2('table.session')}</TableCell>
                <TableCell sx={styles.headerCell}>{t2('table.date')}</TableCell>
                <TableCell sx={styles.headerCell}>{t2('table.time')}</TableCell>
                <TableCell sx={styles.headerCell}>{t2('table.instructor')}</TableCell>
                <TableCell sx={styles.headerCell}>{t2('table.location')}</TableCell>
                <TableCell sx={styles.headerCell}>{t2('table.status')}</TableCell>
                <TableCell sx={{ ...styles.headerCell, width: 130 }}>{t2('table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {group.sessions.map(session => {
                const instructorLabel = session.instructor?.fullName || session.instructorName || '—';
                const isImmutable = !!session.isManuallyCompleted;
                return (
                  <TableRow key={session.id} sx={styles.row} onClick={() => onOpenDetail(session)}>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography sx={styles.titleCell}>{session.title}</Typography>
                      {session.description && (
                        <Typography component="div" sx={styles.description}>
                          {session.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={styles.cell}>{formatSessionDate(session.startDateTime)}</TableCell>
                    <TableCell sx={styles.cell}>{formatSessionTime(session.startDateTime)}</TableCell>
                    <TableCell sx={styles.cell}>{instructorLabel}</TableCell>
                    <TableCell sx={styles.cell}>{session.location || t2('table.noLocation')}</TableCell>
                    <TableCell>
                      <SessionStatusPill status={session.status} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} onClick={e => e.stopPropagation()}>
                        {canUpdate && (
                          <Tooltip title={isImmutable ? t2('immutable.editDisabledTooltip') : t2('detail.edit')}>
                            <span>
                              <IconButton size="small" disabled={isImmutable} onClick={() => onEdit(session)}>
                                <EditIcon
                                  sx={{ fontSize: 18, color: isImmutable ? colors.GRAY : colors.GRAY_DARKER }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title={t2('detail.delete')}>
                            <IconButton size="small" onClick={() => onDelete(session)}>
                              <DeleteIcon sx={{ fontSize: 18, color: colors.RED }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton size="small">
                          <ChevronRightIcon sx={{ fontSize: 18, color: colors.GRAY_DARK }} />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      ))}
    </Box>
  );
}

SessionsMonthlyView.propTypes = {
  sessions: PropTypes.array,
  loading: PropTypes.bool,
  onOpenDetail: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

SessionsMonthlyView.defaultProps = { sessions: [], loading: false };
